import React, { useEffect, useMemo, useState } from 'react';
import { limit, onSnapshot, orderBy, query, setDoc, where } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { activityFeedColl, configDoc, countdownsColl, cycleDoc, dailyAnswerDoc, moodsDoc, nightNotesColl, petDoc, streakDoc, tasksColl } from '../db';
import { UserProfile, Screen } from '../App';
import PetCharacter, { CharacterConfig, DEFAULT_CONFIG } from './PetCharacter';
import { getDayQuestion } from '../utils/dailyQuestions';
import InstantPhotoWidget from './InstantPhotoWidget';
import AiCompanionPanel from './AiCompanionPanel';
import type { ActivityFeedItem } from '../utils/activityFeed';
import { buildWeeklySummary, getWeeklyQuest, getWeeklyQuestProgress } from '../utils/relationshipInsights';
import { CycleState, getCycleInfo } from '../utils/cycle';
import { isFemaleProfile } from '../utils/gender';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }
interface CoupleConfig { startDate: string; }
interface DayData { [uid: string]: { answer: string; name: string }; }
interface PetMiniState { happiness?: number; cleanliness?: number; hunger?: number; bond?: number; lastActive?: any; lastCareAt?: any; lastBondAt?: any; characterConfig?: CharacterConfig; }
interface MoodEntry { mood: string; label: string; note?: string; name?: string; timestamp?: any; }
interface MoodData { [dateKey: string]: { [uid: string]: MoodEntry }; }
interface CountdownItem { id: string; title: string; date: string; }
interface TaskItem { id: string; title: string; completed: boolean; weekKey: string; }
interface NightNoteMini { text: string; userId: string; userName: string; date: string; timestamp?: any; }
interface StreakState { count: number; lastUpdatedDate: string; longestStreak: number; broken: boolean; }

function getTodayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function elapsedFrom(dateStr: string) {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(`${dateStr}T00:00:00`).getTime()) / 1000));
  return {
    days: Math.floor(diff / 86400),
    weeks: Math.floor(diff / 604800),
    months: Math.floor(diff / 2592000),
    hours: Math.floor((diff % 86400) / 3600),
    minutes: Math.floor((diff % 3600) / 60),
    seconds: diff % 60,
  };
}

function twoDigit(value: number) {
  return value.toString().padStart(2, '0');
}

function getWeekKey(date = new Date()) {
  const jan1 = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil(((date.getTime() - jan1.getTime()) / 86400000 + jan1.getDay() + 1) / 7);
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function timestampToDateKey(timestamp: any) {
  const raw = timestamp?.toMillis?.()
    ?? (timestamp?.seconds ? timestamp.seconds * 1000 : timestamp instanceof Date ? timestamp.getTime() : 0);
  if (!raw) return '';
  const d = new Date(raw);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function activityIcon(type: string) {
  if (type === 'question') return '?';
  if (type === 'mood') return '≋';
  if (type === 'night_note') return '✎';
  if (type === 'instant_photo') return '▧';
  if (type === 'heartbeat') return '♡';
  if (type === 'pet_ritual') return '✦';
  if (type === 'pet_care') return '◌';
  return '•';
}

function activityScreen(type: string): Screen {
  if (type === 'question') return 'question';
  if (type === 'mood') return 'mood';
  if (type === 'night_note') return 'notes';
  if (type === 'instant_photo') return 'photos';
  if (type === 'heartbeat') return 'imhere';
  if (type === 'pet_ritual' || type === 'pet_care') return 'pet';
  return 'memories';
}

function petStatus(pet: PetMiniState | null) {
  if (!pet) return { value: '--', detail: 'Bakım alanı hazır' };
  const hasStats = typeof pet.hunger === 'number' && typeof pet.cleanliness === 'number' && typeof pet.happiness === 'number';
  if (!hasStats) return { value: '--', detail: 'Bakım alanı hazır' };
  const hunger = pet.hunger ?? 0;
  const cleanliness = pet.cleanliness ?? 0;
  const happiness = pet.happiness ?? 0;
  const bond = pet.bond ?? 0;
  const value = Math.round((hunger + cleanliness + happiness) / 3);
  if (hunger < 25) return { value, detail: 'Biraz ilgi istiyor' };
  if (cleanliness < 25) return { value, detail: 'Yıkanma zamanı' };
  if (happiness < 25) return { value, detail: 'Oyun bekliyor' };
  if (bond > 80) return { value, detail: 'Size çok bağlı' };
  if (bond > 50) return { value, detail: 'Sizi bekliyor' };
  if (value > 74) return { value, detail: 'Keyfi yerinde' };
  return { value, detail: 'Dengede' };
}

function daysUntil(dateStr: string) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

function toDative(name: string) {
  const lower = name.toLocaleLowerCase('tr-TR');
  const vowels = lower.match(/[aeıioöuü]/g);
  const lastVowel = vowels?.[vowels.length - 1] || 'a';
  const front = ['e', 'i', 'ö', 'ü'].includes(lastVowel);
  const endsWithVowel = /[aeıioöuü]$/i.test(lower);
  return `${name}'${endsWithVowel ? (front ? 'ye' : 'ya') : (front ? 'e' : 'a')}`;
}

export default function Home({ user, navigate }: Props) {
  const [config, setConfig] = useState<CoupleConfig | null>(null);
  const [todayAnswers, setTodayAnswers] = useState<DayData>({});
  const [todayNightNotes, setTodayNightNotes] = useState<NightNoteMini[]>([]);
  const [pet, setPet] = useState<PetMiniState | null>(null);
  const [moods, setMoods] = useState<MoodData>({});
  const [countdowns, setCountdowns] = useState<CountdownItem[]>([]);
  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [streak, setStreak] = useState<StreakState | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);
  const [cycle, setCycle] = useState<CycleState>({});
  const [, setTick] = useState(0);

  const today = getTodayKey();
  const fallbackPartner = user.displayName === 'Serhat' ? 'Ayça' : 'Serhat';

  useEffect(() => {
    const unsub = onSnapshot(configDoc(user.coupleId), snap => {
      if (snap.exists()) setConfig(snap.data() as CoupleConfig);
      else setConfig(null);
    });
    return unsub;
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(dailyAnswerDoc(user.coupleId, today), snap => {
      setTodayAnswers(snap.exists() ? snap.data() as DayData : {});
    });
  }, [today, user.coupleId]);

  useEffect(() => {
    const q = query(nightNotesColl(user.coupleId), where('date', '==', today));
    return onSnapshot(q, snap => {
      setTodayNightNotes(snap.docs.map(d => d.data() as NightNoteMini));
    });
  }, [today, user.coupleId]);

  useEffect(() => {
    return onSnapshot(petDoc(user.coupleId), snap => {
      setPet(snap.exists() ? snap.data() as PetMiniState : null);
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(moodsDoc(user.coupleId), snap => {
      setMoods(snap.exists() ? snap.data() as MoodData : {});
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(streakDoc(user.coupleId), snap => {
      setStreak(snap.exists() ? snap.data() as StreakState : null);
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(countdownsColl(user.coupleId), orderBy('date', 'asc'));
    return onSnapshot(q, snap => {
      setCountdowns(snap.docs.map(d => ({ id: d.id, ...d.data() } as CountdownItem)));
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(tasksColl(user.coupleId), orderBy('timestamp', 'desc'));
    return onSnapshot(q, snap => {
      setTasks(snap.docs.map(d => ({ id: d.id, ...d.data() } as TaskItem)));
    });
  }, [user.coupleId]);

  useEffect(() => {
    const q = query(activityFeedColl(user.coupleId), orderBy('createdAt', 'desc'), limit(20));
    return onSnapshot(q, snap => {
      setActivityFeed(snap.docs.map(d => ({ id: d.id, ...d.data() } as ActivityFeedItem)));
    });
  }, [user.coupleId]);

  useEffect(() => {
    return onSnapshot(cycleDoc(user.coupleId), snap => {
      setCycle(snap.exists() ? snap.data() as CycleState : {});
    });
  }, [user.coupleId]);

  useEffect(() => {
    setDoc(petDoc(user.coupleId), { lastActive: new Date() }, { merge: true }).catch(() => {});
    const id = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [user.coupleId]);

  const elapsed = config?.startDate ? elapsedFrom(config.startDate) : null;
  const answeredToday = Boolean(todayAnswers[user.uid]);
  const partnerAnswered = Object.keys(todayAnswers).some(uid => uid !== user.uid);
  const todayMood = moods[today] || {};
  const myMood = todayMood[user.uid];
  const partnerMood = Object.entries(todayMood).find(([uid]) => uid !== user.uid)?.[1];
  const partnerAnswer = Object.entries(todayAnswers).find(([uid]) => uid !== user.uid)?.[1];
  const partnerAnswerText = partnerAnswer?.answer?.trim();
  const hasNightNote = todayNightNotes.some(note => note.userId === user.uid);
  const partnerNightNote = todayNightNotes.find(note => note.userId !== user.uid);
  const partnerNightNoteText = partnerNightNote?.text?.trim();
  const feedToday = activityFeed.filter(item => item.dayKey === today);
  const partnerFeedItem = activityFeed.find(item => item.actorId && item.actorId !== user.uid && item.actorName);
  const partner = partnerAnswer?.name || partnerMood?.name || partnerNightNote?.userName || partnerFeedItem?.actorName || fallbackPartner;
  const partnerTo = toDative(partner);
  const todayQuestion = getDayQuestion(today);
  const questionState = answeredToday && partnerAnswered
    ? 'İkiniz de yazdınız.'
    : answeredToday
      ? `${partner} yazınca tamam.`
      : partnerAnswered
        ? `${partner} yazmış.`
        : 'Bugünün sorusu.';
  const petMini = petStatus(pet);
  const petConfig = pet?.characterConfig || DEFAULT_CONFIG;
  const petName = petConfig.name || DEFAULT_CONFIG.name;
  const petCareToday = timestampToDateKey(pet?.lastCareAt || pet?.lastBondAt) === today
    || feedToday.some(item => item.actorId === user.uid && (item.type === 'pet_care' || item.type === 'pet_ritual'));
  const sentSignalToday = feedToday.some(item => item.actorId === user.uid && item.type === 'heartbeat');
  const partnerSignalToday = feedToday.find(item => item.actorId !== user.uid && item.type === 'heartbeat');
  const recentActivity = activityFeed.slice(0, 3);
  const todaysCountdown = countdowns.find(item => item.date === today);
  const nextCountdown = countdowns.find(item => daysUntil(item.date) >= 0);
  const openTask = tasks.find(item => item.weekKey === getWeekKey() && !item.completed);
  const plan = todaysCountdown
    ? { label: 'Bugün', title: todaysCountdown.title, detail: 'Takvimde bugün var.', screen: 'countdown' as Screen }
    : nextCountdown
      ? { label: `${daysUntil(nextCountdown.date)} gün kaldı`, title: nextCountdown.title, detail: 'Sıradaki planımız.', screen: 'countdown' as Screen }
      : openTask
        ? { label: 'Bu hafta', title: openTask.title, detail: 'Yapılacaklardan biri.', screen: 'tasks' as Screen }
        : { label: 'Boş gün', title: 'Bugün plan yok.', detail: 'İstersen birlikte bir fikir seçelim.', screen: 'date-ideas' as Screen };
  const weeklyQuest = getWeeklyQuest();
  const weeklyQuestProgress = getWeeklyQuestProgress(activityFeed, weeklyQuest);
  const weeklySummary = buildWeeklySummary(activityFeed, moods);
  const cycleProfiles = Object.values(cycle.profiles || {}).filter(profile =>
    profile.enabled && isFemaleProfile({ displayName: profile.ownerName, gender: profile.ownerGender })
  );
  const cycleTarget = cycleProfiles.find(profile => profile.ownerUid !== user.uid) || cycleProfiles.find(profile => profile.ownerUid === user.uid);
  const cycleInfo = getCycleInfo(cycleTarget);

  const rhythmItems = useMemo(() => ([
    { done: answeredToday, label: 'Soru', detail: partnerAnswered && !answeredToday ? `${partner} yazdı` : 'Günün sorusu', screen: 'question' as Screen },
    { done: Boolean(myMood), label: 'Hal', detail: partnerMood && !myMood ? `${partner} seçti` : 'Ruh hali', screen: 'mood' as Screen },
    { done: hasNightNote, label: 'Not', detail: partnerNightNoteText && !hasNightNote ? `${partner} bıraktı` : 'Gece notu', screen: 'notes' as Screen },
    { done: petCareToday, label: petName, detail: 'Minik ilgisi', screen: 'pet' as Screen },
    { done: sentSignalToday, label: 'İşaret', detail: partnerSignalToday ? `${partner} gönderdi` : 'Buradayım', screen: 'imhere' as Screen },
  ]), [answeredToday, hasNightNote, myMood, partner, partnerAnswered, partnerMood, partnerNightNoteText, petCareToday, petName, sentSignalToday, partnerSignalToday]);

  const completion = Math.round((rhythmItems.filter(item => item.done).length / rhythmItems.length) * 100);

  const focus = useMemo(() => {
    if (!answeredToday) {
      return {
        label: 'Günün sorusu',
        title: partnerAnswered ? `${partner} yazmış.` : 'Bugünün sorusu seni bekliyor.',
        desc: partnerAnswered
          ? 'Sen de yazınca cevaplar yan yana duracak.'
          : 'Kısa yazsan da olur, içinden geçtiği gibi.',
        quote: partnerAnswerText ? `${partner}: "${partnerAnswerText}"` : '',
        cta: 'Cevapla',
        screen: 'question' as Screen,
      };
    }
    if (!myMood) {
      return {
        label: 'Bugünkü halimiz',
        title: 'Bugün sende hangi hal var?',
        desc: 'Bir kelime bile yeter.',
        cta: 'Halini seç',
        screen: 'mood' as Screen,
      };
    }
    if (!hasNightNote) {
      return {
        label: 'Gece notu',
        title: partnerNightNoteText ? `${partner} not bırakmış.` : 'Bugünden bir cümle seç.',
        desc: partnerNightNoteText
          ? 'Sen de yazınca bu gece yan yana duracak.'
          : 'Kısa olabilir. Hatta yarım bile kalabilir.',
        quote: partnerNightNoteText ? `${partner}: "${partnerNightNoteText}"` : '',
        cta: 'Not yaz',
        screen: 'notes' as Screen,
      };
    }
    if (!petCareToday) {
      return {
        label: 'Bugünkü bağ görevi',
        title: `${petName} bugün senden küçük bir iz bekliyor.`,
        desc: 'Bir bakım, bir sevgi ya da günlük ritüel yeter.',
        cta: 'Minik’e git',
        screen: 'pet' as Screen,
      };
    }
    if (!sentSignalToday) {
      return {
        label: 'Bir işaret',
        title: partnerSignalToday ? `${partner} sana işaret bırakmış.` : `${partnerTo} küçük bir işaret gönder.`,
        desc: partnerSignalToday ? 'Bugünün karşılığı sende kalmasın.' : 'Tek dokunuşla aklında olduğunu hissettir.',
        quote: '',
        cta: 'Gönder',
        screen: 'imhere' as Screen,
      };
    }
    return {
      label: 'Gün yerini buldu',
      title: 'Bugün aranızdaki ritim tamamlandı.',
      desc: `${petName} de, bugünün küçük izleri de yerini buldu.`,
      cta: 'Anılara bak',
      screen: 'memories' as Screen,
    };
  }, [answeredToday, hasNightNote, myMood, partnerAnswered, partner, partnerTo, partnerAnswerText, partnerNightNoteText, petCareToday, petName, sentSignalToday, partnerSignalToday]);

  const rhythmHeadline = completion === 100
    ? 'Bugün aranız tamam.'
    : partnerAnswered && !answeredToday
      ? `${partner} seni soruda bekliyor.`
      : partnerMood && !myMood
        ? `${partner} bugünkü halini bıraktı.`
        : partnerNightNoteText && !hasNightNote
          ? `${partner} bu geceye bir cümle bıraktı.`
          : `${5 - rhythmItems.filter(item => item.done).length} küçük adım kaldı.`;

  return (
    <main className="daily-home">
      <header className="daily-top">
        <div>
          <h1>Serhat & Ayça</h1>
        </div>
      </header>

      <motion.section
        className="today-rhythm-hero"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 85, damping: 18 }}
      >
        <button className="today-rhythm-main" onClick={() => navigate('daily-flow')}>
          <div className="today-rhythm-copy">
            <small>{focus.label}</small>
            <h2>{rhythmHeadline}</h2>
            <p>{focus.title}</p>
            {focus.desc && <span>{focus.desc}</span>}
            {'quote' in focus && focus.quote && <em>{focus.quote}</em>}
          </div>
          <div className="today-progress" style={{ '--progress': `${completion}%` } as React.CSSProperties}>
            <span>{completion}</span>
            <small>%</small>
          </div>
          <b>2 dakikalık akış</b>
        </button>

        <div className="rhythm-steps" aria-label="Bugünün ritmi">
          {rhythmItems.map(item => (
            <button
              key={item.label}
              className={item.done ? 'done' : ''}
              onClick={() => navigate(item.screen)}
            >
              <i>{item.done ? '✓' : '·'}</i>
              <strong>{item.label}</strong>
              <span>{item.detail}</span>
            </button>
          ))}
        </div>
      </motion.section>

      <section className="couple-dashboard">
        <div className="days-showcase" aria-label="Birliktelik sayacı">
          <small>Birlikte</small>
          <strong>{elapsed ? elapsed.days : '--'}</strong>
          <span>gün</span>
          {elapsed ? (
            <div className="elapsed-time-detail">
              <time>{twoDigit(elapsed.hours)}:{twoDigit(elapsed.minutes)}:{twoDigit(elapsed.seconds)}</time>
              <small>saat · dakika · saniye</small>
            </div>
          ) : (
            <p>Ayarlardan ekleyelim</p>
          )}
        </div>

        <button className="pet-showcase" onClick={() => navigate('pet')}>
          <div className="pet-stage">
            <PetCharacter config={petConfig} size={112} mood={petMini.value !== '--' && Number(petMini.value) < 30 ? 'sad' : 'happy'} />
          </div>
          <div>
            <small>Bizim Minik</small>
            <strong>{petName}</strong>
            <span>{petMini.detail}</span>
          </div>
        </button>
      </section>

      <section className="daily-panels">
        <button className="mood-panel" onClick={() => navigate('mood')}>
          <div className="panel-title">
            <small>Bugünkü halimiz</small>
            <b>{myMood && partnerMood ? 'İkiniz de seçtiniz' : myMood ? `${partner} bekleniyor` : 'Henüz boş'}</b>
          </div>
          <div className="mood-pair">
            <span>{myMood?.mood || '·'}</span>
            <i />
            <span>{partnerMood?.mood || '·'}</span>
          </div>
          <p>{myMood?.label || user.displayName} · {partnerMood?.label || partner}</p>
        </button>

        <button className="plan-panel" onClick={() => navigate(plan.screen)}>
          <div className="panel-title">
            <small>Bugünkü plan</small>
            <b>{plan.label}</b>
          </div>
          <h3>{plan.title}</h3>
          <p>{plan.detail}</p>
        </button>
      </section>

      {cycleTarget && cycleInfo.active && (
        <button className="cycle-home-panel" onClick={() => navigate('cycle')}>
          <div className="panel-title">
            <small>Döngü takibi</small>
            <b>{cycleTarget.ownerUid === user.uid ? 'Senin ritmin' : `${cycleTarget.ownerName} için destek`}</b>
          </div>
          <div className="cycle-home-orb" style={{ '--cycle-accent': cycleInfo.accent, '--cycle-progress': `${cycleInfo.progress}%` } as React.CSSProperties}>
            <strong>{cycleInfo.day}</strong>
          </div>
          <div>
            <h3>{cycleInfo.title}</h3>
            <p>{cycleInfo.supportHint}</p>
          </div>
        </button>
      )}

      <AiCompanionPanel user={user} />

      <section className="home-weekly-grid">
        <button className="weekly-quest-card" onClick={() => navigate(weeklyQuest.screen as Screen)}>
          <small>Haftalık görev</small>
          <strong>{weeklyQuest.title}</strong>
          <p>{weeklyQuest.detail}</p>
          <div className="quest-bar"><span style={{ width: `${weeklyQuestProgress.percent}%` }} /></div>
          <em>{weeklyQuestProgress.done ? 'Tamamlandı' : `${weeklyQuestProgress.count}/${weeklyQuestProgress.target}`}</em>
        </button>

        <button className="weekly-summary-card" onClick={() => navigate('memories')}>
          <small>Haftalık özet</small>
          <strong>{weeklySummary.title}</strong>
          <p>{weeklySummary.detail}</p>
        </button>
      </section>

      <section className="home-question-card">
        <button onClick={() => navigate('question')}>
          <div className="home-question-top">
            <div>
              <small>Günün sorusu</small>
              <b>{questionState}</b>
            </div>
            <span>Ritmimiz · {streak?.count ? `${streak.count} gün` : 'başlayacak'}</span>
          </div>

          <h2>{todayQuestion}</h2>

          {partnerAnswerText && (
            <p className="home-question-answer">
              <small>{partner}'ın cevabı</small>
              {partnerAnswerText}
            </p>
          )}
        </button>
      </section>

      <section className="home-feed-card">
        <div className="panel-title">
          <small>Bugünden kalanlar</small>
          <b>{recentActivity.length ? 'Son izler' : 'Henüz boş'}</b>
        </div>

        <div className="home-feed-list">
          {recentActivity.length ? recentActivity.map(item => (
            <button key={item.id} onClick={() => navigate(activityScreen(item.type))}>
              <span>{activityIcon(item.type)}</span>
              <div>
                <strong>{item.title}</strong>
                <p>{item.body || `${item.actorName} bugün küçük bir iz bıraktı.`}</p>
                <small>{item.actorName}</small>
              </div>
            </button>
          )) : (
            <button onClick={() => navigate('bond')}>
              <span>✦</span>
              <div>
                <strong>Bugüne ilk izi bırak</strong>
                <p>Bir soru, bir hal ya da Minik’e küçük bir ilgiyle başlayın.</p>
                <small>Bağ</small>
              </div>
            </button>
          )}
        </div>

        <button className="home-feed-more" onClick={() => navigate('memories')}>Geçmişi aç</button>
      </section>

      <InstantPhotoWidget user={user} />

    </main>
  );
}
